import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, usd, formatApiErrorDetail, CARD_PAYMENTS_ENABLED, newKey } from "../lib/api";
import { useRegistration } from "../context/RegistrationContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Heart, ShieldCheck } from "lucide-react";
import { SponsorPackages } from "../components/SponsorPackages";
import { TermsCheckbox } from "../components/TermsCheckbox";
import { BeneficiaryList, SponsorDetailsForm, SponsorPayment, SponsorSummary } from "../components/sponsor/SponsorParts";

const newAllocation = () => ({ _k: newKey(), mode: "organization", nominee: {} });

function sponsorError(type, sponsor) {
  if (!sponsor.email && !sponsor.business_email) return "Please provide a contact email.";
  if (type === "individual" && (!sponsor.first_name || !sponsor.last_name)) return "Please provide your name.";
  if (type === "corporate" && !sponsor.company) return "Please provide the company/organization name.";
  return null;
}

function submitLabel({ closed, submitting, method, total }) {
  if (closed) return "Registration Closed";
  if (submitting) return "Processing...";
  if (method === "stripe") return `Sponsor & Pay ${usd(total)}`;
  return `Sponsor & Get Bank Details (${usd(total)})`;
}

function SponsorHero() {
  return (
    <section className="bg-[#03281B] brand-stripe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="inline-flex items-center gap-2 text-[#F3C242] text-xs font-bold uppercase tracking-[0.2em]"><Heart size={16} /> Youth Sponsorship Program</div>
        <h1 className="font-display text-4xl sm:text-6xl font-extrabold text-white mt-3 tracking-tight">ONE OPPORTUNITY CHANGES EVERYTHING</h1>
        <p className="text-[#F3C242] mt-4 text-sm uppercase tracking-[0.25em]">Support a young athlete. Empower a brighter future.</p>
        <p className="text-white/80 mt-4 max-w-2xl">Help us give more children the chance to experience the unique Sporting CP methodology at Sporting CP Camp Arkansas 2027, develop their skills and confidence, and be part of something bigger on and off the field. Sponsors are recognized for their support unless they choose to remain anonymous.</p>
      </div>
    </section>
  );
}

function PackagesSection({ packages, pkgKey, onSelect, termsUrl }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <h2 className="font-display text-2xl font-extrabold text-[#03281B] mb-6">Choose your sponsorship</h2>
      <SponsorPackages packages={packages} selectedKey={pkgKey} onSelect={onSelect} />
      <div className="grid sm:grid-cols-2 gap-4 mt-8 rounded-2xl bg-[#F2F8F5] p-6">
        <div><div className="font-bold text-[#03281B] uppercase tracking-widest text-sm">Nominate a child</div><p className="text-sm text-slate-600 mt-1">You can nominate a specific participant to sponsor.</p></div>
        <div><div className="font-bold text-[#03281B] uppercase tracking-widest text-sm">Or let us select</div><p className="text-sm text-slate-600 mt-1">Allow Sporting Camps to select a deserving child from our scholarship pool.</p></div>
      </div>
      <p className="text-xs text-slate-400 mt-3">This is a child sponsorship initiative, not a charitable donation, and sponsorship payments are not represented as tax-deductible. Beneficiary eligibility and selection follow the official program regulations{termsUrl ? <> (<a className="underline" href={termsUrl}>view regulations</a>)</> : " (configurable link, provided separately)"}.</p>
    </section>
  );
}

export default function Sponsor() {
  const navigate = useNavigate();
  const { config } = useRegistration();
  const packages = config?.sponsor_packages || [];
  const [type, setType] = useState("individual");
  const [sponsor, setSponsor] = useState({});
  const [anonymous, setAnonymous] = useState(false);
  const [ackName, setAckName] = useState("");
  const [pkgKey, setPkgKey] = useState("single");
  const [allocations, setAllocations] = useState([newAllocation()]);
  const [method, setMethod] = useState(CARD_PAYMENTS_ENABLED ? "stripe" : "bank");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const pkg = packages.find((p) => p.key === pkgKey);
  const qty = pkg?.children || 1;
  const total = pkg?.price || 0;
  const closed = config?.registration_closed;

  const selectPackage = (key) => {
    const n = packages.find((p) => p.key === key)?.children || 1;
    setPkgKey(key);
    setAllocations((prev) => [...prev, ...Array.from({ length: Math.max(0, n - prev.length) }, newAllocation)].slice(0, n));
  };

  const submit = async () => {
    const err = sponsorError(type, sponsor);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsorships", {
        sponsor_type: type, sponsor, anonymous, acknowledgment_name: ackName, package: pkgKey,
        allocations: allocations.map((a) => ({ mode: a.mode, nominee: a.mode === "nominate" ? a.nominee : null })),
        payment_method: method, origin_url: window.location.origin, terms_accepted_at: new Date().toISOString(),
      });
      if (method !== "stripe") { toast.success("Sponsorship submitted!"); navigate(`/sponsorship/${data.reference}`); return; }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Unable to start Stripe checkout.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const label = submitLabel({ closed, submitting, method, total });

  return (
    <div>
      <Navbar />
      <SponsorHero />
      <PackagesSection packages={packages} pkgKey={pkgKey} onSelect={selectPackage} termsUrl={config?.legal?.terms_url} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6" data-testid="sponsor-form">
          <BeneficiaryList allocations={allocations} setAllocations={setAllocations} />
          <SponsorDetailsForm type={type} setType={setType} sponsor={sponsor} setSponsor={setSponsor}
            ackName={ackName} setAckName={setAckName} anonymous={anonymous} setAnonymous={setAnonymous} />
          <SponsorPayment method={method} setMethod={setMethod} bank={config?.bank_details || {}} />
          <TermsCheckbox id="sponsor-accept-terms" testid="sponsor-accept-terms-checkbox" termsTestid="sponsor-terms-link" privacyTestid="sponsor-privacy-link" checked={acceptedTerms} onChange={setAcceptedTerms} />
          <button data-testid="sponsor-submit" onClick={submit} disabled={submitting || closed || !acceptedTerms} className="w-full rounded-full bg-[#008057] hover:bg-[#054D34] disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 font-bold text-lg flex items-center justify-center gap-2">
            <ShieldCheck size={20} /> {label}
          </button>
        </div>
        <div className="lg:col-span-1"><SponsorSummary pkg={pkg} qty={qty} total={total} /></div>
      </section>
      <Footer camp={config} />
    </div>
  );
}
